import { useEffect, useState, useRef } from "react";
import useFocoModal from "../../hooks/useFocoModal";
import Icon from "../Icon";
import { BtnPrimary } from "../Atoms";
import * as api from "../../api";
import { getTipoColor, getTipoIcon, fmtHours } from "../../utils";
import { AG_ORIGENES } from "../../constants";

// «¿Has terminado por hoy?» — el cierre del día.
//
// Sale al fichar salida con tiempo cronometrado sin anotar. Si se confirma, cada
// cronómetro del día se convierte en una entrada del cuaderno (una por tarea,
// sumando sus tramos) y, para los que cuelgan de un paso del proyecto, se
// pregunta si la tarea quedó terminada para cerrarla y desbloquear el flujo.
//
// Los tiempos que se pintan están CONGELADOS: el `pausarTodo` ya corrió antes de
// fichar. No hay ningún setInterval aquí a propósito — un número que corre
// mientras decides es justo lo que no se quiere.
export default function CierreJornadaModal({ agendaTiposData = [], onConfirmar,
                                             onDespues, guardando }) {
  const cajaRef = useRef(null);
  useFocoModal(cajaRef, true, onDespues);
  const [resumen, setResumen] = useState(null);
  const [fallo,   setFallo]   = useState(null);
  // Desmarcadas por defecto: cerrar un paso avanza el flujo del proyecto y
  // desbloquea a otros. Es la dirección difícil de deshacer, y lo que se pidió
  // fue preguntar, no asumir.
  const [cerrar,  setCerrar]  = useState({});

  useEffect(() => {
    let vivo = true;
    api.getResumenJornada()
      .then(r => { if (vivo) setResumen(r); })
      .catch(e => { if (vivo) setFallo(e.message || "No se pudo leer el resumen del día"); });
    return () => { vivo = false; };
  }, []);

  const cronos = resumen?.cronometros || [];
  const total = cronos.reduce((s, c) => s + (c.minutos || 0), 0);
  const marcadas = Object.keys(cerrar).filter(k => cerrar[k]);

  return (
    <div className="modal-bg"
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.38)",
        backdropFilter: "blur(3px)", zIndex: 170, display: "flex",
        alignItems: "center", justifyContent: "center", padding: 20 }}
      onClick={onDespues}>
      <div ref={cajaRef} role="dialog" aria-modal="true" className="modal-box"
        style={{ background: "var(--surface)", borderRadius: 16, boxShadow: "var(--shadow-lg)",
          width: "100%", maxWidth: 520, padding: 24, maxHeight: "90vh", overflowY: "auto" }}
        onClick={e => e.stopPropagation()}>

        <h3 style={{ margin: "0 0 4px", fontWeight: 700, fontSize: 16,
          display: "flex", alignItems: "center", gap: 8 }}>
          <Icon name="hourglass-split" size={15} color="var(--accent)" />
          ¿Has terminado por hoy?
        </h3>
        <p style={{ margin: "0 0 18px", fontSize: 11.5, color: "var(--text3)", lineHeight: 1.5 }}>
          Has fichado salida con {cronos.length} tarea{cronos.length === 1 ? "" : "s"} cronometrada
          {cronos.length === 1 ? "" : "s"}. Si has terminado, el tiempo se anota en tu Cuaderno:
          una entrada por tarea, sumando sus tramos. Podrás ajustarlo después.
        </p>

        {fallo && (
          <div style={{ padding: "9px 12px", borderRadius: 9, border: "1px solid var(--red-line)",
            background: "var(--red-bg)", fontSize: 11.5, color: "#991b1b", marginBottom: 14 }}>
            {fallo}
          </div>
        )}

        {!resumen && !fallo && (
          <div style={{ fontSize: 12, color: "var(--text3)", padding: "10px 0" }}>
            Preparando el resumen del día…
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
          {cronos.map(c => {
            const cl = getTipoColor(c.type, [], agendaTiposData);
            const ic = getTipoIcon(c.type, agendaTiposData);
            const org = AG_ORIGENES[c.origen];
            return (
              <div key={c.id} style={{ padding: "10px 12px", borderRadius: 10,
                background: "var(--surface2)", border: "1px solid var(--border)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                  {ic ? <Icon name={ic} size={12} color={cl} />
                      : <span style={{ width: 7, height: 7, borderRadius: "50%",
                          background: cl, flexShrink: 0 }} />}
                  <span style={{ flex: 1, fontSize: 13, fontWeight: 600, overflow: "hidden",
                    textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.description}</span>
                  <span style={{ fontSize: 13, fontWeight: 800, color: cl, whiteSpace: "nowrap",
                    fontVariantNumeric: "tabular-nums" }}>{fmtHours((c.minutos || 0) / 60)}</span>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 4,
                  flexWrap: "wrap", fontSize: 10.5, color: "var(--text3)" }}>
                  <span>{c.type}</span>
                  {c.origen !== "ninguno" && org && (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 4,
                      padding: "1px 7px", borderRadius: 20, background: cl + "18",
                      border: `1px solid ${cl}44`, color: cl, fontWeight: 700 }}>
                      <Icon name={org.icon} size={9} color={cl} />
                      {c.incidencia_ref ? `#${c.incidencia_ref}` : (c.etiqueta || org.label)}
                    </span>
                  )}
                </div>

                {/* Solo se pregunta por lo que cuelga de un paso abierto Y que
                    esta persona puede modificar: preguntar algo que no se puede
                    hacer es un callejón sin salida. */}
                {c.preguntar_finalizar && (
                  <label style={{ display: "flex", alignItems: "flex-start", gap: 7,
                    marginTop: 9, cursor: "pointer" }}>
                    <input type="checkbox" checked={!!cerrar[c.task_id]}
                      onChange={e => setCerrar(p => ({ ...p, [c.task_id]: e.target.checked }))}
                      style={{ marginTop: 2, cursor: "pointer" }} />
                    <span style={{ fontSize: 11.5, color: "var(--text2)", lineHeight: 1.45 }}>
                      La tarea está terminada — ciérrala en el proyecto
                      <span style={{ display: "block", fontSize: 10.5, color: "var(--text3)" }}>
                        Pasa a Completada
                        {c.desbloquearia?.length
                          ? ` y desbloquea ${c.desbloquearia.map(x => `«${x.title}»`).join(", ")}.`
                          : "."}
                      </span>
                    </span>
                  </label>
                )}
              </div>
            );
          })}
        </div>

        {!!cronos.length && (
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8,
            fontSize: 12, color: "var(--text3)", marginBottom: 16 }}>
            Total del día
            <b style={{ color: "var(--accent)", fontSize: 13 }}>{fmtHours(total / 60)}</b>
          </div>
        )}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
          {/* Descartar no escribe nada: los cronómetros se quedan en pausa en el
              servidor, sobreviven a la recarga y el widget lo recuerda con un
              aviso. Sin esa vuelta, el tiempo del día se perdería en silencio. */}
          <button className="btn" onClick={onDespues}
            style={{ padding: "8px 16px", borderRadius: 9, border: "1px solid var(--border)",
              cursor: "pointer", fontSize: 13, background: "var(--surface2)",
              color: "var(--text2)", fontFamily: "var(--font)" }}>
            No he terminado, solo salgo un rato
          </button>
          <BtnPrimary onClick={() => onConfirmar(marcadas)} disabled={!resumen || guardando}>
            {guardando ? "Anotando en el cuaderno…" : "Sí, cerrar la jornada"}
          </BtnPrimary>
        </div>
      </div>
    </div>
  );
}
